"use client";

import { useTranslations } from "next-intl";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";

import { Input } from "@/common/components/form/input";
import { InputBadge } from "@/common/components/form/input-badge";
import { useStoreProduct } from "@/features/product/contexts/product-context";
import { formatPrice, useFormattedPrice } from "@/features/product/hooks/use-price";
import { CalculatedData, CalculatedFields } from "./calculated-fields";
import { getMarkupFromSalePriceWithVat, getPrices } from "./utils";

enum CalculatorField {
  Markup = "markup",
  SalePrice = "salePrice",
  Vat = "vat",
}

type MarkupCalculatorFormProps = {
  markupDefault: number;
};

export function MarkupCalculatorForm({ markupDefault }: MarkupCalculatorFormProps) {
  const t = useTranslations();
  const { pricingResult } = useStoreProduct();
  const unitPrice = useFormattedPrice(pricingResult?.salePrice.unit!);
  const [prices, setPrices] = useState<ReturnType<typeof getPrices>>();
  const {
    register,
    formState: { errors },
    trigger,
    getValues,
    setValue,
    reset,
  } = useForm<{
    [CalculatorField.Markup]: number;
    [CalculatorField.Vat]: number;
    [CalculatorField.SalePrice]: number;
  }>();

  useEffect(() => {
    if (!pricingResult) return;
    const markup = markupDefault || 0;
    // To do, include tax calculation
    const prices = getPrices(pricingResult?.salePrice.total!, markup, 0);
    setPrices(prices);
    reset({
      salePrice: prices.salePriceWithVat,
      markup,
      vat: 0,
    });
  }, [markupDefault, pricingResult, reset]);

  const calculated = useMemo<CalculatedData>(() => {
    const { price } = pricingResult || {};
    if (!price || !prices) return { retailPrice: "0", profit: "0", margin: "0" };
    const { salePrice, markupPrice } = prices;
    const margin = (markupPrice / salePrice) * 100;

    return {
      retailPrice: formatPrice({ ...price, centAmount: salePrice }),
      profit: formatPrice({ ...price, centAmount: markupPrice }),
      margin: `${margin.toFixed(price.fractionDigits || 2)}%`,
    };
  }, [pricingResult, prices]);

  const handleVatAndMarkupChange = () => {
    const [markup, vat] = getValues([CalculatorField.Markup, CalculatorField.Vat]);
    const prices = getPrices(pricingResult?.salePrice.total!, markup, vat);
    setPrices((prevState) => ({ ...prevState, ...prices }));
    setValue(CalculatorField.SalePrice, prices.salePriceWithVat, {
      shouldValidate: true,
    });
  };

  const handlePriceWithVatChange = () => {
    const [salePriceWithVat, vat] = getValues([CalculatorField.SalePrice, CalculatorField.Vat]);
    const markup = getMarkupFromSalePriceWithVat(
      pricingResult?.salePrice.total!,
      salePriceWithVat,
      vat,
    );
    const prices = getPrices(pricingResult?.salePrice.total!, markup, vat);
    setPrices((prevState) => ({
      ...prevState,
      ...prices,
      salePriceWithVat,
    }));
    setValue(CalculatorField.Markup, markup, { shouldValidate: true });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const calculatorField = e.target.name as CalculatorField;
    trigger(calculatorField);
    switch (calculatorField) {
      case CalculatorField.Markup:
      case CalculatorField.Vat:
        return handleVatAndMarkupChange();
      case CalculatorField.SalePrice:
        return handlePriceWithVatChange();
    }
  };

  return (
    <section className="flex w-full flex-col items-start">
      <p className="text-grey-90 text text-base leading-7">
        {t("common.text-markup-calculator-description")}
      </p>

      <div className="mt-5 flex w-full flex-col justify-between 2xl:flex-row">
        <form noValidate className="flex w-full flex-col justify-center 2xl:mr-12 2xl:w-1/2">
          <Input
            label={t("common.text-markup") + ":"}
            variant="solid"
            type="number"
            {...register(CalculatorField.Markup, {
              valueAsNumber: true,
              required: t("forms.markup-required"),
              validate: {
                isPositive: (v) => Number(v) >= 0 || t("forms.markup-positive"),
              },
              onChange: handleInputChange,
            })}
            error={errors.markup && t(errors.markup.message!)}
            className="w-32"
            inputClassName="!pl-2 !pr-12"
            labelClassName="!text-base !leading-7 !text-grey-90 !font-normal"
            postfixNode={<InputBadge value="%" />}
          />

          <div className="mt-3 flex items-end justify-start">
            <Input
              label={`${t("common.text-retail-price")}: (${t("common.text-optional")} ${t(
                "common.text-vat",
              )})`}
              type="number"
              variant="solid"
              {...register(CalculatorField.SalePrice, {
                valueAsNumber: true,
                onChange: handleInputChange,
              })}
              className="w-32"
              inputClassName="!pr-2 !pl-12"
              labelClassName="!text-base !leading-7 !text-grey-90 !font-normal relative w-max"
              prefixNode={<InputBadge value={unitPrice[0] || "$"} />}
            />

            <div className="flex items-center justify-center pl-2">
              <span className="pr-2">{t("common.text-inc")}</span>

              <Input
                variant="solid"
                type="number"
                {...register(CalculatorField.Vat, {
                  valueAsNumber: true,
                  onChange: handleInputChange,
                })}
                className="w-32"
                inputClassName="!pl-2 !pr-12"
                labelClassName="!text-base !leading-7 !text-grey-90 !font-normal"
                postfixNode={<InputBadge value="%" />}
              />

              <span className="text-grey-90 pl-2 text-base font-normal leading-7">
                {t("common.text-vat")}
              </span>
            </div>
          </div>
        </form>

        <CalculatedFields
          className="border-grey-40 mt-5 flex w-full flex-col justify-center border-y border-dashed 2xl:mt-0 2xl:w-1/2"
          unitPrice={unitPrice}
          calculated={calculated}
        />
      </div>
    </section>
  );
}
